import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  reason: "consultation",
  subject: "",
  message: "",
};

const ContactSection = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm(initialForm);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-padding bg-[#0A192F] text-white relative overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 bg-grid-navy opacity-50" aria-hidden="true"></div>
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-[#FF5A1F]/15 blur-[120px]" aria-hidden="true"></div>

      <div className="container-page relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="chip-light mb-5">{t.contact.eyebrow}</span>
            <h2
              data-testid="contact-title"
              className="font-display font-black text-4xl sm:text-5xl leading-[1.05]"
            >
              {t.contact.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/75 leading-relaxed">
              {t.contact.lead}
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={SITE.phoneHref}
                data-testid="contact-phone"
                className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#FF5A1F] text-white">
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
                    {lang === "fr" ? "Prise de rendez-vous" : "Appointment booking"}
                  </p>
                  <p className="mt-1 font-display font-bold text-lg">{SITE.phone}</p>
                </div>
              </a>

              <a
                href={SITE.assistantPhoneHref}
                data-testid="contact-assistant-phone"
                className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-white/10 text-[#FF5A1F]">
                  <i className="fa-solid fa-user-tie" aria-hidden="true"></i>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
                    {lang === "fr" ? `Assistante — ${SITE.assistantName}` : `Assistant — ${SITE.assistantName}`}
                  </p>
                  <p className="mt-1 font-display font-bold text-base">{SITE.assistantPhone}</p>
                  <p className="text-xs text-white/70 mt-0.5 break-all">{SITE.assistantEmail}</p>
                </div>
              </a>

              <a
                href={SITE.emailHref}
                data-testid="contact-email"
                className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#FF5A1F] text-white">
                  <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
                    {lang === "fr" ? "Email du Dr Aswad" : "Dr Aswad's email"}
                  </p>
                  <p className="mt-1 font-display font-bold text-base break-all">{SITE.email}</p>
                </div>
              </a>
              <div
                data-testid="contact-address"
                className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/5 p-5"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#FF5A1F] text-white">
                  <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
                    {t.contact.addressLabel}
                  </p>
                  <p className="mt-1 font-display font-bold text-lg">Clinique Juge</p>
                  <p className="text-sm text-white/70">{SITE.address}</p>
                </div>
              </div>
            </div>

            <a
              href={SITE.doctolibUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-doctolib"
              className="mt-8 inline-flex w-full justify-center items-center gap-2 rounded-md bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-navy transition hover:bg-[#FF5A1F] hover:text-white"
            >
              <i className="fa-solid fa-calendar-plus" aria-hidden="true"></i>
              Réserver sur Doctolib
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-2xl bg-white p-6 md:p-8 shadow-2xl text-navy"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.firstName} *
                  </label>
                  <input
                    required
                    name="first_name"
                    value={form.first_name}
                    onChange={onChange}
                    data-testid="input-first-name"
                    className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.lastName} *
                  </label>
                  <input
                    required
                    name="last_name"
                    value={form.last_name}
                    onChange={onChange}
                    data-testid="input-last-name"
                    className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.email} *
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="input-email"
                    className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.phone}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="input-phone"
                    className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 transition"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.reason}
                  </label>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {t.contact.reasonOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`cursor-pointer rounded-md border px-3 py-2.5 text-xs font-semibold text-center transition ${
                          form.reason === opt.value
                            ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F]"
                            : "border-border bg-white text-foreground/70 hover:border-muted-foreground/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="reason"
                          value={opt.value}
                          checked={form.reason === opt.value}
                          onChange={onChange}
                          data-testid={`radio-reason-${opt.value}`}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.subject} *
                  </label>
                  <input
                    required
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    data-testid="input-subject"
                    className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 transition"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                    {t.contact.message} *
                  </label>
                  <textarea
                    required
                    rows={5}
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    data-testid="input-message"
                    className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 transition resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-6 btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                {submitting ? t.contact.submitting : t.contact.submit}
              </button>

              <p className="mt-4 text-[11px] text-muted-foreground">
                Vos données sont traitées de manière confidentielle, conformément au RGPD, et utilisées uniquement dans le cadre de votre demande.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
